<template>
  <div class="d-flex gap-3 my-3 entity-files">
    <div v-for="file in currentFiles" class="flex-1">
      <Text size="p1" class="text-center">{{ file.name }}</Text>

      <div class="file-upload-progress-container mt-3" v-if="file.file_name">
        <FileUploadProgress
          :fileName="file.file_name"
          :uploadProgress="100"
          :downloadFileFunction="() => handleDownload(file)"
        >
          <template #file-name>
            <div
              @click="() => updateShowManagementModal(file)"
              class="file-name pointer flex-1"
              data-cy="file-info"
            >
              {{ file.file_name }}
            </div>
          </template>
        </FileUploadProgress>
        <div class="mt-2">
          <i class="fa fa-calendar mr-1" />
          {{ formatDate(file.created_at) }}
        </div>
      </div>
      <div
        v-else
        @click="() => updateShowManagementModal(file)"
        class="mt-3 px-3 w-100 dashed-border align-items-center pointer"
      >
        <div class="d-flex align-items-center mx-auto h-100 upload-container">
          <div class="mr-2">
            <i class="fa fa-folder" />
          </div>
          <div class="text p3 color-primary upload-label file-input">
            Upload File
          </div>
        </div>
      </div>
    </div>
    <FileManagement
      v-if="!!selectedFile"
      :file-id="fileId"
      ref="file_management"
      :upload-query="{ ...fileQuery, upload_name: selectedFile?.name }"
      :custom-allowed-extensions="allowedExtensions"
      :custom-uploaded-files="customUploadedFiles"
      ctaPositionalClasses="display-none"
      @closeModal="closeModal"
      @handleFileUploaded="handleFileUploaded"
      @use="(file_name) => useFile(file_name, selectedFile?.name)"
      @delete="fetchEntity"
    />
  </div>
</template>

<script>
import { nextTick } from "vue";
import moment from "moment";
import { EntitiesService } from "@/services/entity";
import { FilesService } from "@/services/files";
import FileManagement from "../fileManagement/FileManagement";

export default {
  name: "EntityFiles",
  props: {
    entityId: String,
    testId: String,
    quarter: String,
  },
  components: {
    FileManagement,
  },
  data() {
    return {
      uploadFiles: [
        { name: "BG" },
        { name: "CET" },
        { name: "RF" },
        { name: "Délai de règlement" },
      ],
      entity: {},
      fileId: 3,
      allowedExtensions: [],
      selectedFile: undefined,
    };
  },
  computed: {
    customUploadedFiles() {
      return (this.entity?.entity_files || [])
        .map((entity_file) => {
          return {
            ...entity_file,
            filename: entity_file.file_name,
            modified: entity_file.created_at,
          };
        })
        .filter(
          (item, index, array) =>
            index === array.findIndex((t) => t.filename === item.filename)
        );
    },
    currentFiles() {
      const testFiles = Object.fromEntries(
        (this.entity?.entity_files || [])
          .filter(
            (file) =>
              file.quarter === this.quarter &&
              file.test_id === this.testId.replaceAll(".", "")
          )
          .map((file) => [file.upload_name, file])
      );
      return this.uploadFiles.map((file) => ({
        ...file,
        ...(testFiles[file.name] || {}),
      }));
    },
    fileQuery() {
      return {
        test_id: this.testId,
        entity_id: this.entityId,
        quarter: this.quarter,
      };
    },
  },
  mounted() {
    this.fetchEntity();
    this.fetchAllowedExtensions();
  },
  methods: {
    async updateShowManagementModal(file) {
      this.selectedFile = file;
      await nextTick();
      this.$refs.file_management.updateShowManagementModal();
    },
    formatDate(date) {
      return moment.utc(date).local().format("YYYY-MM-DD HH:mm:ss");
    },
    async fetchEntity() {
      this.entity = await EntitiesService.getEntity(this.entityId, true);
    },
    async fetchAllowedExtensions() {
      this.allowedExtensions = await FilesService.getAllowedExtensions(
        this.fileId
      );
    },
    async handleFileUploaded() {
      await this.fetchEntity();
      this.closeModal();
    },
    closeModal() {
      this.selectedFile = undefined;
    },
    async useFile(file_name, upload_name) {
      const new_date = this.formatDate(new Date());
      let new_entity_files = [];
      const file_was_uploaded_for_file_name = !!this.entity.entity_files.filter(
        (file) =>
          file.file_name === file_name &&
          file.upload_name === upload_name &&
          file.quarter === this.quarter
      ).length;

      if (file_was_uploaded_for_file_name) {
        new_entity_files = this.entity.entity_files
          .map((file) => {
            const update_file_flag =
              file.file_name === file_name &&
              file.upload_name === upload_name &&
              file.quarter === this.quarter;
            return {
              ...file,
              created_at: update_file_flag ? new_date : file.created_at,
            };
          })
          .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      } else {
        const reuse_file = {
          ...this.entity.entity_files.filter(
            (file) => file.file_name === file_name
          )[0],
        };
        reuse_file.upload_name = upload_name;
        new_entity_files = [...this.entity.entity_files, reuse_file];
      }

      const { res, data } = await EntitiesService.editOrCreateEntity(
        this.entity.code,
        {
          entity_files: new_entity_files,
        }
      );
      if (res.status === 200) await this.handleFileUploaded();
      else alert(data.detail);
    },
    handleDownload(file) {
      this.$downloadFile({
        name: file.file_name,
        fileId: this.fileId,
        query: this.fileQuery,
      });
    },
    async handleDelete(file) {
      const isDeleted = await this.$deleteResource({
        resourceName: file.name,
        query: {
          ...this.fileQuery,
          upload_name: file.name,
          name: file.file_name || file.name,
          file_id: this.fileId,
        },
      });
      if (isDeleted) {
        this.fetchEntity();
      }
    },
  },
};
</script>

<style lang="scss">
.display-none {
  display: none;
}

.entity-files .file-management {
  position: absolute;

  tbody {
    padding-bottom: 20px;
  }
}
</style>

<style lang="scss" scoped>
.file-upload-progress-container {
  border: 1px solid $primary;
}
.dashed-border {
  height: 58px;
  border: 1px dashed $primary !important;
}
.upload-container {
  width: 105px;
}
</style>
